import { ref, computed, unref, type MaybeRef, readonly } from 'vue'
import { useOCRStore } from '@/shared/store'
import { OCDocTypeOCR } from '@/shared/types/api.types'
import { getGender, parseDDMMYYYY, checkImageDimensions } from './useOCR.utils'
import { OCRCallbacks, OCRResult } from './useOCR.types'
import { useI18n } from 'vue-i18n'
import { OCR_SUPPORTED_COUNTRIES } from './useOCR.constants'

export const useOCR = (iso?: MaybeRef<string | undefined>, callbacks?: OCRCallbacks) => {
  const ocrStore = useOCRStore()
  const { t } = useI18n()

  const uploadResult = ref<'error' | 'success' | undefined>(undefined)
  const errorMessage = ref('')
  const isProcessing = ref(false)
  const abortController = ref<AbortController | null>(null)
  const isErrorFileSize = ref(false)

  const currentIso = computed(() => unref(iso)?.toUpperCase() ?? '')

  const isOCRSupported = computed(() => OCR_SUPPORTED_COUNTRIES.includes(currentIso.value))

  const getErrorMessage = (errorCode?: string): string => {
    switch (errorCode) {
      case 'ocr_failed':
        return t('visitors.fill_visitors_info.file_recognition.file_quality_error')
      case 'aspect_ratio_failed':
        return t('visitors.fill_visitors_info.file_recognition.file_resolution_error')
      case 'file_size_error':
        return t('visitors.fill_visitors_info.file_recognition.file_size_error')
      default:
        return t('visitors.fill_visitors_info.file_recognition.anoud_error')
    }
  }

  const validateFile = async (
    file: File,
    minWidth = 640,
    minHeight = 480
  ): Promise<{ isValid: boolean; error?: string }> => {
    if (!isOCRSupported.value) {
      return { isValid: false, error: t('OCR is not supported for this country') }
    }

    try {
      const isValidDimensions = await checkImageDimensions(file, minWidth, minHeight)
      if (!isValidDimensions) {
        const error = getErrorMessage('aspect_ratio_failed')
        callbacks?.onValidationError?.(error)
        return { isValid: false, error }
      }
    } catch {
      const error = getErrorMessage()
      callbacks?.onValidationError?.(error)
      return { isValid: false, error }
    }

    return { isValid: true }
  }

  const startOCRProcessing = (): void => {
    abortController.value = new AbortController()
    isProcessing.value = true
    uploadResult.value = undefined
    errorMessage.value = ''
  }

  const finishOCRProcessing = (): void => {
    isProcessing.value = false
    abortController.value = null
  }

  const handleOCRSuccess = (response: {
    familyName: string
    firstName: string
    passportNumber: string
    gender: string
    dateOfBirth: string
    dateOfExpiry: string
  }): OCRResult => {
    const result: OCRResult = {
      familyName: response.familyName,
      firstName: response.firstName,
      passportNumber: response.passportNumber,
      gender: getGender(response.gender) as 'male' | 'female',
      dateOfBirth: parseDDMMYYYY(response.dateOfBirth),
      dateOfExpiry: parseDDMMYYYY(response.dateOfExpiry),
    }

    uploadResult.value = 'success'
    callbacks?.onSuccess?.(result)
    return result
  }

  const handleOCRError = (error: unknown): void => {
    uploadResult.value = 'error'
    // @ts-expect-error
    const errorMsg = getErrorMessage(error.response?.data?.errorCode)
    errorMessage.value = errorMsg
    callbacks?.onError?.(errorMsg)
  }

  const processFile = async (file: File): Promise<OCRResult | null> => {
    isErrorFileSize.value = false

    const validation = await validateFile(file)
    if (!validation.isValid) {
      errorMessage.value = validation.error || ''
      uploadResult.value = 'error'
      return null
    }

    const docType: OCDocTypeOCR = currentIso.value === 'IN' ? 'IND' : 'PAK'

    if (isErrorFileSize.value) {
      errorMessage.value = getErrorMessage('file_size_error')
      uploadResult.value = 'error'
      return null
    }

    startOCRProcessing()

    try {
      const response = await ocrStore.passportOCR(file, docType, abortController.value!)

      if (abortController.value?.signal.aborted) return null

      return handleOCRSuccess(response)
    } catch (error: unknown) {
      if (abortController.value?.signal.aborted) return null

      handleOCRError(error)
      return null
    } finally {
      finishOCRProcessing()
    }
  }

  const cancelRequest = (callback?: () => void): void => {
    callback?.()
    if (!abortController.value) return

    abortController.value.abort()
    isProcessing.value = false
    uploadResult.value = undefined
    errorMessage.value = ''
  }

  const setErrorFileSize = (): void => {
    isErrorFileSize.value = true
  }

  return {
    uploadResult,
    errorMessage: readonly(errorMessage),
    isProcessing: readonly(isProcessing),
    processFile,
    cancelRequest,
    setErrorFileSize,
  }
}
